import React, { useState, useMemo } from 'react';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { Sparkles, ChevronDown, ChevronUp, ClipboardList, Brain, CircleCheck, TriangleAlert, ChartNoAxesColumn } from 'lucide-react';

const getScorePathColor = (val) => {
  const n = parseFloat(val) || 0;
  if (n >= 60) return '#34B53A';
  if (n >= 30) return '#FFB200';
  return '#DC0000';
};

const getScoreTrailColor = (val) => {
  const n = parseFloat(val) || 0;
  if (n >= 60) return 'rgba(52,181,58,0.15)';
  if (n >= 30) return 'rgba(255,178,0,0.15)';
  return 'rgba(220,0,0,0.15)';
};

// Mirrors CodingResult's pill colors (independently defined, not imported).
export const RANK_STYLE = {
  Best: 'bg-green-50 text-green-600 border-green-500/30 dark:bg-[rgba(0,255,0,0.1)] dark:text-[#34B53A] dark:border-[rgba(0,255,0,0.3)]',
  Good: 'bg-blue-50 text-blue-600 border-blue-200/60 dark:bg-blue-400/10 dark:text-blue-300 dark:border-blue-400/25',
  Weak: 'bg-orange-50 text-orange-600 border-orange-500/30 dark:bg-[rgba(245,158,11,0.1)] dark:text-[#f59e0b] dark:border-[rgba(245,158,11,0.3)]',
  Poor: 'bg-red-50 text-red-600 border-red-200 dark:bg-red-400/10 dark:text-red-400 dark:border-red-400/30',
};

// Distinct from RANK_STYLE and the AI Insights violet/slate palette — competency
// identity is a third, separate signal.
const COMPETENCY_DOT_COLORS = ['bg-teal-500', 'bg-amber-500', 'bg-pink-500', 'bg-cyan-500', 'bg-fuchsia-500', 'bg-lime-600'];

// Handles both the current flat-string shape and the older {competency, note} shape
// stored on reports generated before this schema changed — prefers the short
// competency name over the old full-sentence note, since this renders as a pill.
const pillText = (item) => (typeof item === 'string' ? item : item?.competency || item?.note || '');

const SectionHeader = ({ icon, label, themeMode, className = '', labelClassName = 'text-sm text-textSecondary dark:text-[#C9A3FF]' }) => (
  <div className={`flex items-center ${className}`}>
    {icon}
    <span className={`ml-2 ${labelClassName}`}>{label}</span>
    <div className={`ml-3 flex-1 border-t ${themeMode === 'dark' ? 'border-gray-700/50' : 'border-gray-200'}`} />
  </div>
);

/**
 * Score ring, AI Insights (descriptive only — no verdict), and per-question
 * evidence — purely presentational, shared by BehavioralResult.jsx (recruiter,
 * self-fetching) and PublicReport.jsx (public/share-token, pre-fetched). Both
 * pass the same shapes: competency_scores/questions/ai_insights straight from
 * /get_behavioral_test_result and get_public_report respectively.
 */
export default function BehavioralAssessmentPanel({
  overallScore,
  competencyScores = [],
  questions = [],
  insights,
  themeMode,
  candidateName,
}) {
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [activeSection, setActiveSection] = useState('insights');

  const nameByCompetencyId = useMemo(
    () => Object.fromEntries(competencyScores.map((c) => [c.competency_id, c.competency_name])),
    [competencyScores]
  );

  const dotColorByCompetencyId = useMemo(
    () => Object.fromEntries(
      competencyScores.map((c, i) => [c.competency_id, COMPETENCY_DOT_COLORS[i % COMPETENCY_DOT_COLORS.length]])
    ),
    [competencyScores]
  );

  // Preserves authored order; each competency's questions stay together instead of
  // one flat 1-N list, matching how Skill Performance Breakdown is already grouped.
  const questionGroups = useMemo(() => {
    const groups = [];
    const indexByCompetency = new Map();
    questions.forEach((q, i) => {
      if (!indexByCompetency.has(q.competency_id)) {
        indexByCompetency.set(q.competency_id, groups.length);
        groups.push({ competency_id: q.competency_id, questions: [] });
      }
      groups[indexByCompetency.get(q.competency_id)].questions.push({ ...q, _number: i + 1 });
    });
    return groups;
  }, [questions]);

  const toggleQuestion = (questionId) => {
    setExpandedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
  };

  return (
    <div className="space-y-4 pb-6">
      {/* ── Section tabs, same pill style as the Interview report's Smart Evaluation / Candidate Response ── */}
      <div className={`inline-flex gap-2 rounded-full p-1 border ${themeMode === 'dark' ? 'border-gray-600 bg-gray-800/40' : 'border-gray-600 bg-white'}`}>
        {[
          { key: 'insights', label: 'AI Insights' },
          { key: 'responses', label: 'Questions & Responses' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveSection(tab.key)}
            className={
              activeSection === tab.key
                ? 'py-2 px-4 bg-gradient-to-l from-[#FB54AE] to-[#2F64FF] rounded-full text-white text-xs sm:text-sm font-medium'
                : 'py-2 px-4 text-gray-500 dark:text-gray-400 rounded-full font-medium text-xs sm:text-sm'
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeSection === 'insights' && (
      <div>
        {insights ? (
          <>
            {insights.summary && (
              <p className="text-sm leading-relaxed text-gray-700 dark:text-[#CCCCCC]">
                {insights.summary}
              </p>
            )}

            {/* Ring + pills, same layout/spacing as the Interview report's Score/Top Skills/Improvement Areas row */}
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row items-center sm:items-stretch gap-8 py-3">
                <div className="w-full sm:w-[18%] flex items-center justify-center flex-shrink-0">
                  <div className="relative flex-shrink-0" style={{ width: 160, height: 160 }}>
                    <CircularProgressbar
                      value={overallScore}
                      text={`${Math.round(overallScore)}%`}
                      circleRatio={0.75}
                      styles={buildStyles({
                        rotation: 1 / 2 + 1 / 8,
                        strokeLinecap: 'butt',
                        pathColor: getScorePathColor(overallScore),
                        trailColor: getScoreTrailColor(overallScore),
                        textColor: themeMode === 'dark' ? '#FFFFFF' : '#1F2937',
                        textSize: '24px',
                        fontWeight: '600',
                      })}
                    />
                    <div className="absolute bottom-2 left-0 right-0 text-center text-xs text-gray-500 dark:text-gray-400 font-medium">
                      Score
                    </div>
                  </div>
                </div>

                <div className="w-full sm:w-[75%] justify-center flex flex-col gap-2.5">
                  {insights.strengths?.length > 0 && (
                    <>
                      <SectionHeader
                        icon={<CircleCheck className="w-5 h-5 text-violet-600 dark:text-[#C9A3FF] flex-shrink-0" />}
                        label="Strengths"
                        themeMode={themeMode}
                      />
                      <div className="flex flex-wrap gap-2">
                        {insights.strengths.map((s, i) => (
                          <span
                            key={i}
                            className="text-sm px-4 py-2 rounded-full border border-violet-500/30 bg-violet-50 text-violet-700 dark:border-violet-400/25 dark:bg-violet-400/10 dark:text-violet-300"
                          >
                            {pillText(s)}
                          </span>
                        ))}
                      </div>
                    </>
                  )}

                  {insights.growth_areas?.length > 0 && (
                    <>
                      <SectionHeader
                        icon={<TriangleAlert className="w-5 h-5 text-violet-600 dark:text-[#C9A3FF] flex-shrink-0" />}
                        label="Growth Areas"
                        themeMode={themeMode}
                        className={insights.strengths?.length > 0 ? 'mt-6' : ''}
                      />
                      <div className="flex flex-wrap gap-2">
                        {insights.growth_areas.map((g, i) => (
                          <span
                            key={i}
                            className="text-sm px-4 py-2 rounded-full border border-slate-400/40 bg-slate-100 text-slate-700 dark:border-slate-400/25 dark:bg-slate-400/10 dark:text-slate-300"
                          >
                            {pillText(g)}
                          </span>
                        ))}
                      </div>
                    </>
                  )}

                  {!insights.strengths?.length && !insights.growth_areas?.length && !insights.is_fallback && (
                    <p className="text-sm text-gray-500 dark:text-[#A8A8A8]">
                      Fairly even performance — no standout strengths or growth areas this round.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {competencyScores.length > 0 && (
              <>
                <SectionHeader
                  icon={<ChartNoAxesColumn className="w-5 h-5 text-violet-600 dark:text-[#C9A3FF] flex-shrink-0" />}
                  label="Skill Performance Breakdown"
                  themeMode={themeMode}
                  className="mt-5"
                />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 mt-5">
                  {competencyScores.map((c) => (
                    <div
                      key={c.competency_id}
                      className="flex flex-col items-center mb-6 w-[150px] mx-auto justify-between border border-[#52525280] dark:glass-effect bg-bgPrimary rounded-2xl p-4"
                    >
                      <div className="text-xs text-center text-gray-500 dark:text-[#A8A8A8] mb-2">
                        {c.competency_name}
                      </div>
                      <div className="h-[100px] w-[100px]">
                        <CircularProgressbar
                          value={c.score}
                          text={`${c.score}%`}
                          circleRatio={0.75}
                          styles={buildStyles({
                            rotation: 0.625,
                            strokeLinecap: 'butt',
                            pathColor: getScorePathColor(c.score),
                            trailColor: getScoreTrailColor(c.score),
                            textColor: themeMode === 'dark' ? '#FFFFFF' : '#1F2937',
                            textSize: '18px',
                          })}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {insights.thinking_style?.length > 0 && (
              <>
                <SectionHeader
                  icon={<Brain className="w-5 h-5 text-violet-600 dark:text-[#C9A3FF] flex-shrink-0" />}
                  label="Thinking Style"
                  themeMode={themeMode}
                  className="mt-5"
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  {insights.thinking_style.map((tag, i) => (
                    <span
                      key={i}
                      className="text-sm px-3 py-1 rounded-full border border-violet-500/30 bg-violet-50 text-violet-700 dark:border-violet-400/25 dark:bg-violet-400/10 dark:text-violet-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Sparkles className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm text-gray-500 dark:text-[#A8A8A8]">
              No AI insights available for this attempt.
            </p>
          </div>
        )}
      </div>
      )}

      {activeSection === 'responses' && (
      <div>
        {questions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <ClipboardList className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm text-gray-500 dark:text-[#A8A8A8]">
              No responses found for {candidateName || 'this candidate'}.
            </p>
          </div>
        ) : (
          <div className="mt-3 space-y-6">
            {questionGroups.map((group) => {
              const dotColor = dotColorByCompetencyId[group.competency_id] || COMPETENCY_DOT_COLORS[0];
              return (
                <div key={group.competency_id}>
                  <SectionHeader
                    icon={<span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotColor}`} />}
                    label={nameByCompetencyId[group.competency_id] || 'Uncategorized'}
                    themeMode={themeMode}
                    className="mb-3"
                    labelClassName="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-[#A8A8A8]"
                  />
                  <div className="space-y-3">
                    {group.questions.map((q) => {
                      const picked = (q.options || []).find((o) => o.id === q.selected_option_id);
                      const isExpanded = expandedQuestions.has(q.id || q._number);

                      return (
                        <div
                          key={q.id || q._number}
                          className={`rounded-2xl border overflow-hidden transition-colors ${
                            isExpanded
                              ? themeMode === 'dark' ? 'border-gray-700/50 bg-white/5' : 'border-gray-200 bg-white'
                              : themeMode === 'dark' ? 'border-gray-700/40 bg-transparent' : 'border-gray-200 bg-transparent'
                          }`}
                        >
                          <button
                            onClick={() => toggleQuestion(q.id || q._number)}
                            className={`w-full flex items-center gap-3 px-5 py-3.5 text-left transition-colors ${
                              themeMode === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                            }`}
                          >
                            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-400/15 text-violet-700 dark:text-violet-300 text-sm font-bold flex items-center justify-center">
                              {q._number}
                            </span>
                            <span className="flex-1 text-sm font-semibold text-gray-900 dark:text-white text-left truncate">
                              {q.scenario_text}
                            </span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {picked ? (
                                <span className={`text-sm px-3 py-1 rounded-full font-semibold border ${RANK_STYLE[picked.rank] || RANK_STYLE.Weak}`}>
                                  {picked.rank}
                                </span>
                              ) : (
                                <span className="text-sm px-3 py-1 rounded-full border bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-700/50 dark:text-gray-400 dark:border-gray-600/50">
                                  Not Answered
                                </span>
                              )}
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                              )}
                            </div>
                          </button>

                          {isExpanded && (
                            <div className={`px-5 pb-4 pt-1 border-t ${themeMode === 'dark' ? 'border-gray-700/40' : 'border-gray-100'}`}>
                              <div className="mt-3 space-y-2">
                                {(q.options || []).map((option) => {
                                  const isPicked = option.id === q.selected_option_id;
                                  const showBestMarker = option.rank === 'Best' && !isPicked;
                                  return (
                                    <div
                                      key={option.id}
                                      className={`text-sm flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-lg border ${
                                        isPicked
                                          ? RANK_STYLE[option.rank] || RANK_STYLE.Weak
                                          : showBestMarker
                                          ? 'border-green-400/50 dark:border-green-400/30 text-gray-700 dark:text-[#CCCCCC]'
                                          : 'text-gray-600 dark:text-[#A8A8A8] border-transparent'
                                      }`}
                                    >
                                      <span>{option.text}</span>
                                      <span className="flex items-center gap-2 flex-shrink-0">
                                        {isPicked && (
                                          <span className="text-xs font-semibold">Their answer</span>
                                        )}
                                        {showBestMarker && (
                                          <span className="text-xs font-semibold text-green-600 dark:text-green-400">Best answer</span>
                                        )}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
